"use client";

/**
 * Navbar renderer for the canvas
 * Shows a non-interactive navbar with links generated from sections
 * Responsive: hamburger menu on mobile, horizontal links on desktop
 */

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { resolveCtaButtonTextColor } from "../../../_logic/utils/cta-button-colors";
import { useSections } from "../../../_hooks/useSections";
import { useNavOverflow } from "../../../_hooks/useNavOverflow";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { useCanvasFieldClick } from "../../../_hooks/useCanvasFieldClick";
import { useFooterPagesPublic } from "../../../_hooks/useFooterPagesPublic";
import {
  DEFAULT_NAV_LABELS,
  DEFAULT_NAVBAR_CUSTOM_COLOR,
  MOBILE_MENU_MAX_HEIGHT_CSS,
  NAV_OVERFLOW_MORE_LABEL,
  NAVBAR_CTA_FIELD_ID,
  NAVBAR_DESKTOP_GAP_PX,
  NAVBAR_GROUP_MIN_GAP_PX,
  NAVBAR_HEIGHT_PX,
  NAVBAR_ICON_LOGO_MAX_W_PX,
  NAVBAR_WORDMARK_LOGO_MAX_W_PX,
} from "../../../_logic/constants";
import { getUniqueAnchorId, isNavigableSection, isSafeUrl } from "../../../_logic/utils";
import { getNavbarCtaButtons, resolveNavButtonColors } from "../../../_logic/utils/navbar-buttons";
import { scrollToAnchor } from "../../../_logic/utils/scroll-utils";
import { resolveThemedSurface } from "../../../_logic/themed-surface";
import type { NavbarComponent, PageComponent } from "../../../_types";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { SmartButton } from "../../public/SmartButton";
import { SmartLink } from "../../public/SmartLink";
import { EmailLinkIndicator } from "./_shared/EmailLinkIndicator";
import { NavDropdown } from "./_shared/NavDropdown";
import { NavOverflowMenu } from "./_shared/NavOverflowMenu";
import type { NavItem } from "./_shared/nav-types";

interface NavbarRendererProps {
  component: NavbarComponent;
}

/** A navbar CTA button normalized for rendering. */
interface ResolvedNavButton {
  /** Stable React key / identity (the button's id). */
  key: string;
  text: string;
  link?: string;
  /** Steps that run after `link` (see `CtaButton.linkedSteps`). */
  linkedSteps?: string[];
  /** Solid background color, resolved from the button's color mode. */
  bgColor: string;
  /** Contrast-aware text color for `bgColor`. */
  textColor: string;
}

/**
 * Generates navbar items from section components.
 * Sections whose nav label is used as a `navGroup` by children become dropdown triggers.
 * Child sections are nested under their parent's dropdown.
 */
function getNavItems(components: PageComponent[]): NavItem[] {
  const visible = components.filter(isNavigableSection);

  // Collect which navGroup labels exist so we know which sections are parents
  const parentLabels = new Set<string>();
  for (const c of visible) {
    if (c.navGroup) parentLabels.add(c.navGroup);
  }

  const items: NavItem[] = [];
  const groupMap = new Map<string, { label: string; id: string }[]>();
  const insertedGroups = new Set<string>();

  for (const c of visible) {
    const label = c.navigationLabel || DEFAULT_NAV_LABELS[c.type];
    // Must match the anchor id emitted by PublicComponentRenderer.
    const anchorId = getUniqueAnchorId(c, components)!;

    if (c.navGroup) {
      // Child section — add to its parent's dropdown
      if (!groupMap.has(c.navGroup)) groupMap.set(c.navGroup, []);
      groupMap.get(c.navGroup)!.push({ label, id: anchorId });
      if (!insertedGroups.has(c.navGroup)) {
        insertedGroups.add(c.navGroup);
        items.push({ type: "dropdown", label: c.navGroup, children: groupMap.get(c.navGroup)! });
      }
    } else if (parentLabels.has(label)) {
      // Parent section — create dropdown with itself as first child
      if (!groupMap.has(label)) groupMap.set(label, []);
      groupMap.get(label)!.unshift({ label, id: anchorId });
      if (!insertedGroups.has(label)) {
        insertedGroups.add(label);
        items.push({ type: "dropdown", label, children: groupMap.get(label)! });
      }
    } else {
      // Regular section — flat link
      items.push({ type: "link", label, id: anchorId });
    }
  }
  return items;
}

/**
 * Non-interactive navbar renderer for the canvas editor
 * Reads sections from the store to generate nav links
 * Returns null when navbar is hidden (visible: false)
 */
export function NavbarRenderer({ component }: NavbarRendererProps): React.ReactElement | null {
  // Hooks
  const sections = useSections();
  const { primaryColor, eventId, parentPageId, currentSubpageSlug } = useBuilderContext();
  const { isPublic } = usePublicViewContext();
  const fieldClick = useCanvasFieldClick(component.id);

  // States
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Gate the body-portal until after mount so SSR doesn't touch `document`.
  const [isMounted, setIsMounted] = useState(false);
  // Shared with FooterRenderer via in-memory request cache — both components consume
  // the same parent+subpages payload per parent_page_id rather than each issuing its
  // own round-trip. Not gated on `showSubpagesInNav` so toggling doesn't refetch.
  const { pages: navPages } = useFooterPagesPublic(parentPageId, isPublic && !!parentPageId);

  // Refs
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Derived values
  const sectionNavItems = getNavItems(sections);
  // BuilderContext's `parentSlug` is the current page's slug when viewing a subpage,
  // so derive the real parent slug from the fetched pages list (mirrors FooterRenderer).
  const trueParentSlug = useMemo(
    () => navPages.find((p) => p.id === parentPageId)?.slug ?? null,
    [navPages, parentPageId]
  );
  const currentPageSlug = currentSubpageSlug ?? trueParentSlug;

  const pageNavItems = useMemo<NavItem[]>(() => {
    if (!component.showSubpagesInNav || !trueParentSlug || navPages.length === 0) return [];
    return navPages.map<NavItem>((p) => {
      const isParent = p.id === parentPageId;
      // Use the short URL form (rewritten back to the long form by middleware) so the address bar
      // stays clean — e.g. `/yc-cancun` instead of `/262/landing-page/yc-cancun`.
      const href = isParent
        ? `/${encodeURIComponent(trueParentSlug)}`
        : `/${encodeURIComponent(trueParentSlug)}/${encodeURIComponent(p.slug)}`;
      return { type: "page-link", label: p.name, href, isCurrent: p.slug === currentPageSlug };
    });
  }, [component.showSubpagesInNav, navPages, currentPageSlug, parentPageId, trueParentSlug]);

  // Page links render BEFORE section anchor links so the main page is the first item in the navbar.
  // A `divider` item is inserted between the two groups only when both are present.
  const navItems = useMemo<NavItem[]>(() => {
    if (pageNavItems.length > 0 && sectionNavItems.length > 0) {
      return [...pageNavItems, { type: "divider" }, ...sectionNavItems];
    }
    return [...pageNavItems, ...sectionNavItems];
  }, [pageNavItems, sectionNavItems]);

  // Background + contrast-aware colors. "white" keeps fixed gray text (legacy default);
  // "primary" / "custom" delegate to the shared themed-surface helper (footer pattern).
  const colorMode = component.colorMode ?? "white";
  const themedSurface = useMemo(
    () =>
      colorMode === "white"
        ? null
        : resolveThemedSurface({
            mode: colorMode === "custom" ? "custom" : "primary",
            customColor: component.customColor,
            primaryColor,
            defaultCustomColor: DEFAULT_NAVBAR_CUSTOM_COLOR,
          }),
    [colorMode, component.customColor, primaryColor]
  );
  const navBgColor = themedSurface?.backgroundColor ?? "#ffffff";
  const linkColor = themedSurface?.mutedTextColor ?? "#4b5563"; // gray-600 in white mode
  const linkHoverColor = themedSurface?.textColor ?? "#111827"; // gray-900 in white mode
  const borderColor = themedSurface?.borderColor ?? "#f3f4f6"; // gray-100 in white mode

  // CTA buttons — the reorderable `ctaButtons` array (legacy single CTA migrated transparently),
  // normalized into one render-ready list. Buttons default to the navbar's section-wide color but
  // may individually override it via their own `color` field.
  const ctaButtons = useMemo<ResolvedNavButton[]>(() => {
    const { bgColor: sectionBgColor } = resolveNavButtonColors(
      component.buttonColorMode,
      component.buttonCustomColor,
      primaryColor
    );
    return getNavbarCtaButtons(component).map((btn) => {
      const bgColor = btn.color ?? sectionBgColor;
      return {
        key: btn.id,
        text: btn.label,
        link: btn.url,
        linkedSteps: btn.linkedSteps,
        bgColor,
        textColor: resolveCtaButtonTextColor(btn, bgColor),
      };
    });
  }, [
    component.ctaButtons,
    component.ctaText,
    component.ctaLink,
    component.buttonColorMode,
    component.buttonCustomColor,
    primaryColor,
  ]);
  // Mobile overlay logo (icon → logo → default), mirrors the navbar's mobile precedence.
  const safeLogoUrl = component.logoUrl && isSafeUrl(component.logoUrl) ? component.logoUrl : null;
  const safeIconUrl = component.iconUrl && isSafeUrl(component.iconUrl) ? component.iconUrl : null;
  const mobileLogoSrc = safeIconUrl ?? safeLogoUrl ?? "/logo.svg";

  const navPaddingClass = isPublic ? "sm:px-6" : "@sm:px-6";
  const mobileLogoClass = isPublic ? "md:hidden" : "@md:hidden";
  const desktopLogoClass = isPublic ? "md:block" : "@md:block";
  const desktopNavClass = isPublic ? "md:flex" : "@md:flex";
  const desktopCtaClass = isPublic ? "md:flex" : "@md:flex";
  const mobileOnlyClass = isPublic ? "md:hidden" : "@md:hidden";
  // Desktop switches the row from mobile's 2-item flex (logo, hamburger) to a 3-column grid
  // (logo | nav links | CTA) so the nav links center on the row independent of logo/CTA width.
  // `justify-between` stays on unconditionally — it's the mobile layout mechanism (flex, 2 items)
  // AND still does real work at desktop: the grid's outer columns are fixed pixel widths (not
  // `1fr`, see `sideColumnPx` in `useNavOverflow` for why), so unless the row is packed exactly to
  // capacity there's real leftover space at the container level, and `justify-between` is what
  // distributes it symmetrically around the equal-width side columns to keep the middle column
  // centered — do not remove it as "dead" once `grid` is active.
  const desktopNavGridClass = isPublic ? "md:grid" : "@md:grid";

  // Desktop logo precedence per tier: wordmark prefers the wide logo, icon prefers the compact mark.
  const desktopWordmarkSrc = safeLogoUrl ?? safeIconUrl ?? "/logo.svg";
  const desktopIconSrc = safeIconUrl ?? safeLogoUrl ?? "/logo.svg";

  // Content-aware ("priority+") fit measurement: how many links fit inline, whether to overflow into
  // a "More" dropdown, and which logo variant the space affords. Re-measures on resize and whenever
  // the measured content (labels, CTA text, logo URLs) changes.
  const navRevalidateKey = useMemo(
    () =>
      [
        navItems.map((it) => (it.type === "divider" ? "|" : it.label)).join(","),
        ctaButtons.map((b) => b.text).join("|"),
        safeLogoUrl ?? "",
        safeIconUrl ?? "",
      ].join("::"),
    [navItems, ctaButtons, safeLogoUrl, safeIconUrl]
  );
  const { containerRef, measureRef, ctaRef, visibleCount, hasOverflow, logoVariant, sideColumnPx, measured } =
    useNavOverflow(navRevalidateKey);

  const desktopLogoSrc = logoVariant === "icon" ? desktopIconSrc : desktopWordmarkSrc;
  const desktopLogoMaxW = logoVariant === "icon" ? NAVBAR_ICON_LOGO_MAX_W_PX : NAVBAR_WORDMARK_LOGO_MAX_W_PX;

  // Grid column widths for the desktop row (inert under mobile's `flex`). `columnGap` is bound to
  // `NAVBAR_GROUP_MIN_GAP_PX` (also used by `fits()`'s `groupGaps`) rather than a separate Tailwind
  // class, so the visual gap and the fit algorithm's assumed gap can't silently diverge. It applies
  // unconditionally (mobile included) rather than only at the desktop breakpoint — harmless there,
  // since it just sets a floor under `justify-between`'s already-larger spacing between the mobile
  // logo and hamburger button.
  const desktopGridStyle = useMemo<React.CSSProperties>(
    () => ({
      gridTemplateColumns: measured && sideColumnPx > 0 ? `${sideColumnPx}px auto ${sideColumnPx}px` : "1fr auto 1fr",
      columnGap: NAVBAR_GROUP_MIN_GAP_PX,
    }),
    [measured, sideColumnPx]
  );

  // Split the items into the inline run and the overflow tail. Until the first measurement we render
  // ALL links (the row is `invisible`, so nothing flashes) so they exist in the SSR/initial DOM; once
  // measured we collapse to what fits. Trim a trailing divider so the inline row never ends on a
  // separator; the overflow menu drops dividers on its own.
  const visibleItems = useMemo<NavItem[]>(() => {
    if (!measured) return navItems;
    const slice = navItems.slice(0, visibleCount);
    while (slice.length > 0 && slice[slice.length - 1].type === "divider") slice.pop();
    return slice;
  }, [measured, navItems, visibleCount]);
  const overflowItems = useMemo<NavItem[]>(
    () => (measured && hasOverflow ? navItems.slice(visibleCount) : []),
    [measured, hasOverflow, navItems, visibleCount]
  );

  // Handlers
  const handleAnchorClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    // Offsets by the navbar AND the live schedule banner stacked above it (see scrollToAnchor).
    scrollToAnchor(sectionId);
    setIsMobileMenuOpen(false);
  }, []);

  const handleLogoClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (window.location.hash) {
      window.history.pushState(null, "", window.location.pathname + window.location.search);
    }
    setIsMobileMenuOpen(false);
  }, []);

  const handleLogoKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleLogoClick();
      }
    },
    [handleLogoClick]
  );

  // Logo div props differ in the public view (acts as a "scroll to top" button)
  // vs the editor (opens the navbar-logo edit field). Consolidated so the JSX stays scannable.
  const logoProps = isPublic
    ? {
        role: "button" as const,
        tabIndex: 0,
        "aria-label": "Scroll to top",
        onClick: handleLogoClick,
        onKeyDown: handleLogoKeyDown,
      }
    : { onClick: fieldClick("navbar-logo") };

  const handleTransitionEnd = useCallback(() => {
    const el = mobileMenuRef.current;
    if (!el) return;
    if (isMobileMenuOpen) {
      // Keep the viewport cap after the open animation so the wrapper can't grow taller
      // than the visible area (otherwise tall menus push past the URL bar on iOS).
      el.style.maxHeight = MOBILE_MENU_MAX_HEIGHT_CSS;
    }
  }, [isMobileMenuOpen]);

  // Effects

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const el = mobileMenuRef.current;
    if (!el) return;

    if (isMobileMenuOpen) {
      // Cap the animated target to the visible viewport so the open menu fits on screen.
      const viewportCap = Math.max(0, window.innerHeight - NAVBAR_HEIGHT_PX);
      el.style.maxHeight = Math.min(el.scrollHeight, viewportCap) + "px";
    } else {
      const viewportCap = Math.max(0, window.innerHeight - NAVBAR_HEIGHT_PX);
      el.style.maxHeight = Math.min(el.scrollHeight, viewportCap) + "px";
      // Force reflow so the browser registers the starting value
      el.offsetHeight;
      el.style.maxHeight = "0px";
    }
  }, [isMobileMenuOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (!isPublic) return;

    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      document.documentElement.style.overscrollBehavior = "none";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.overscrollBehavior = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.overscrollBehavior = "";
    };
  }, [isMobileMenuOpen, isPublic]);

  // Effects — publish the sticky navbar's height as a CSS var while it's present in the public view,
  // so the anchor-scroll offset (getStickyHeaderOffset) and scroll-padding can account for it. The
  // value is removed when the navbar isn't rendered (hidden / not on the page), so pages without a
  // navbar read 0 instead of over-offsetting. Mirrors the live banner's `--lp-live-banner-height`;
  // together the two vars describe the full sticky chrome stacked at the top of the page.
  useEffect(() => {
    if (!isPublic || !component.visible) return;
    const root = document.documentElement;
    root.style.setProperty("--lp-navbar-height", `${NAVBAR_HEIGHT_PX}px`);
    return () => {
      root.style.removeProperty("--lp-navbar-height");
    };
  }, [isPublic, component.visible]);

  // Don't render when hidden
  if (!component.visible) return null;

  // Shared mobile link list — rendered inside the public overlay and the editor accordion.
  const mobileMenuLinks = (
    <>
      {navItems.length === 0 && !isPublic && (
        <span className="block px-3 py-2 text-sm font-medium text-gray-400">Add sections to see nav links</span>
      )}
      {navItems.map((item, idx) => {
        if (item.type === "divider") {
          return (
            <div
              key={`divider-${idx}`}
              aria-hidden="true"
              className="mx-3 my-2 h-px"
              style={{ backgroundColor: linkColor, opacity: 0.25 }}
            />
          );
        }
        if (item.type === "dropdown") {
          return (
            <div key={item.label}>
              {/* Dropdown group label */}
              <span className="block px-3 py-2 font-medium text-gray-900">{item.label}</span>
              {/* Dropdown children as indented items */}
              {item.children.map((child) =>
                isPublic ? (
                  <a
                    key={child.id}
                    href={`#${child.id}`}
                    onClick={(e) => handleAnchorClick(e, child.id)}
                    className="flex min-h-[44px] touch-manipulation items-center px-3 py-2 pl-8 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900"
                  >
                    {child.label}
                  </a>
                ) : (
                  <span
                    key={child.id}
                    className="flex min-h-[44px] items-center px-3 py-2 pl-8 text-sm font-medium text-gray-500"
                  >
                    {child.label}
                  </span>
                )
              )}
            </div>
          );
        }
        if (item.type === "page-link") {
          return isPublic ? (
            <a
              key={item.href}
              href={item.href}
              className={`flex min-h-[44px] touch-manipulation items-center px-3 py-2 transition-colors hover:text-gray-900 ${item.isCurrent ? "font-bold text-gray-900" : "font-medium text-gray-600"}`}
            >
              {item.label}
            </a>
          ) : (
            <span key={item.href} className="flex min-h-[44px] items-center px-3 py-2 font-medium text-gray-600">
              {item.label}
            </span>
          );
        }
        return isPublic ? (
          <a
            key={item.id}
            href={`#${item.id}`}
            onClick={(e) => handleAnchorClick(e, item.id)}
            className="flex min-h-[44px] touch-manipulation items-center px-3 py-2 font-medium text-gray-600 transition-colors hover:text-gray-900"
          >
            {item.label}
          </a>
        ) : (
          <span key={item.id} className="flex min-h-[44px] items-center px-3 py-2 font-medium text-gray-600">
            {item.label}
          </span>
        );
      })}
      {/* Mobile CTA buttons */}
      {ctaButtons.map((btn) =>
        isPublic && btn.link ? (
          <div key={btn.key} className="px-3 py-2">
            <SmartButton
              href={btn.link}
              label={btn.text}
              primaryColor={btn.bgColor}
              eventId={eventId}
              buttonId={btn.key}
              linkedSteps={btn.linkedSteps}
              className="flex w-full items-center justify-center rounded-xl px-4 py-2 text-center text-sm font-semibold transition-opacity [@media(hover:hover)]:hover:opacity-90"
              style={{ backgroundColor: btn.bgColor, color: btn.textColor }}
            />
          </div>
        ) : !isPublic ? (
          <div key={btn.key} className="px-3 py-2">
            <div
              className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl px-4 py-2 text-center text-sm font-semibold transition-opacity hover:opacity-80"
              style={{ backgroundColor: btn.bgColor, color: btn.textColor }}
              onClick={fieldClick(NAVBAR_CTA_FIELD_ID)}
            >
              <EmailLinkIndicator href={btn.link} />
              {btn.text}
            </div>
          </div>
        ) : null
      )}
    </>
  );

  // A single inline desktop nav item (link / dropdown / page-link / divider). Labels are
  // `whitespace-nowrap` so they never wrap mid-phrase when the row is tight.
  const renderDesktopNavItem = (item: NavItem, idx: number): React.ReactElement => {
    if (item.type === "divider") {
      return (
        <span
          key={`divider-${idx}`}
          aria-hidden="true"
          className="h-4 w-px self-center"
          style={{ backgroundColor: linkColor, opacity: 0.35 }}
        />
      );
    }
    if (item.type === "dropdown") {
      return <NavDropdown key={item.label} label={item.label} children={item.children} isPublic={isPublic} />;
    }
    if (item.type === "page-link") {
      return isPublic ? (
        <a
          key={item.href}
          href={item.href}
          className="whitespace-nowrap text-sm font-semibold transition-opacity hover:opacity-80"
          style={{
            color: item.isCurrent ? linkHoverColor : linkColor,
            fontWeight: item.isCurrent ? 700 : undefined,
          }}
        >
          {item.label}
        </a>
      ) : (
        <span key={item.href} className="whitespace-nowrap text-sm font-semibold" style={{ color: linkColor }}>
          {item.label}
        </span>
      );
    }
    return isPublic ? (
      <a
        key={item.id}
        href={`#${item.id}`}
        onClick={(e) => handleAnchorClick(e, item.id)}
        className="whitespace-nowrap text-sm font-semibold transition-opacity hover:opacity-80"
        style={{ color: linkColor }}
      >
        {item.label}
      </a>
    ) : (
      <span key={item.id} className="whitespace-nowrap text-sm font-semibold" style={{ color: linkColor }}>
        {item.label}
      </span>
    );
  };

  // Lightweight width-equivalent of an inline item for the hidden measurer (no handlers/listeners).
  // Matches each visible item's rendered width so the fit math is accurate.
  const renderMeasureItem = (item: NavItem, idx: number): React.ReactElement => {
    if (item.type === "divider") return <span key={`m-divider-${idx}`} className="h-4 w-px" />;
    return (
      <span key={`m-${idx}`} className="flex items-center gap-1 whitespace-nowrap text-sm font-semibold">
        {item.label}
        {item.type === "dropdown" && <ChevronDown className="h-3.5 w-3.5" />}
      </span>
    );
  };

  // Render
  return (
    <nav
      className="sticky z-40 border-b"
      style={{ top: "var(--lp-live-banner-height, 0px)", backgroundColor: navBgColor, borderColor }}
    >
      <div
        ref={containerRef}
        className={`flex h-14 items-center justify-between px-4 ${navPaddingClass} ${desktopNavGridClass}`}
        style={desktopGridStyle}
      >
        {/* Logo (Nowadays dog logo by default) */}
        {(() => {
          // Mobile: icon → logo → default. Desktop: tier-driven (wordmark when there's room, icon when tight).
          const mobileSrc = mobileLogoSrc;
          const logoImages = (
            <>
              <img
                src={mobileSrc}
                alt="Logo"
                className={`h-6 w-auto max-w-[160px] object-contain ${mobileLogoClass}`}
              />
              <img
                src={desktopLogoSrc}
                alt="Logo"
                className={`hidden h-6 w-auto object-contain ${desktopLogoClass}`}
                style={{ maxWidth: desktopLogoMaxW }}
              />
            </>
          );

          // On the public page, a configured logo link wins over the default scroll-to-top
          // behavior and routes through SmartLink (anchor / page / form / external).
          if (isPublic && component.logoLink) {
            return (
              <SmartLink
                href={component.logoLink}
                eventId={eventId}
                primaryColor={primaryColor}
                className="flex items-center gap-2 justify-self-start transition-opacity hover:opacity-80"
                ariaLabel="Go to linked page"
              >
                {logoImages}
              </SmartLink>
            );
          }

          return (
            <div
              className="flex cursor-pointer items-center gap-2 justify-self-start transition-opacity hover:opacity-80"
              {...logoProps}
            >
              {logoImages}
            </div>
          );
        })()}

        {/* Desktop nav links (content-aware "priority+" row — overflow spills into the More menu).
            Hidden until the first fit-measurement so the overflowing bar never flashes. */}
        <div className={`hidden items-center gap-6 ${desktopNavClass} ${measured ? "" : "invisible"}`}>
          {navItems.length > 0 && (
            <>
              {visibleItems.map((item, idx) => renderDesktopNavItem(item, idx))}
              {overflowItems.length > 0 && (
                <NavOverflowMenu
                  label={NAV_OVERFLOW_MORE_LABEL}
                  items={overflowItems}
                  isPublic={isPublic}
                  triggerColor={linkColor}
                />
              )}
            </>
          )}
        </div>

        {/* Desktop CTA buttons — flex row so multiple buttons sit side by side with a gap.
            `inline-flex items-center` keeps form-link <button>s and external-link <a>s the same height. */}
        <div ref={ctaRef} className={`hidden shrink-0 items-center gap-3 justify-self-end ${desktopCtaClass}`}>
          {ctaButtons.length > 0 ? (
            ctaButtons.map((btn) =>
              isPublic && btn.link ? (
                <SmartButton
                  key={btn.key}
                  href={btn.link}
                  label={btn.text}
                  primaryColor={btn.bgColor}
                  eventId={eventId}
                  buttonId={btn.key}
                  linkedSteps={btn.linkedSteps}
                  className="inline-flex items-center whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition-opacity [@media(hover:hover)]:hover:opacity-90"
                  style={{ backgroundColor: btn.bgColor, color: btn.textColor }}
                />
              ) : (
                <div
                  key={btn.key}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-80"
                  style={{ backgroundColor: btn.bgColor, color: btn.textColor }}
                  onClick={fieldClick(NAVBAR_CTA_FIELD_ID)}
                >
                  <EmailLinkIndicator href={btn.link} />
                  {btn.text}
                </div>
              )
            )
          ) : !isPublic ? (
            <div className="inline-flex items-center whitespace-nowrap rounded-xl border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-400">
              CTA
            </div>
          ) : null}
        </div>

        {/* Mobile hamburger button */}
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`flex touch-manipulation items-center justify-center rounded-md p-2 text-gray-600 transition-colors hover:text-gray-900 ${mobileOnlyClass}`}
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>

        {/* Hidden mirror row that the priority+ fit measurement reads (see useNavOverflow). Always
            rendered, fully out of flow and invisible, so it never affects layout or the live bar. */}
        <div
          ref={measureRef}
          aria-hidden="true"
          className="pointer-events-none invisible absolute left-0 top-0 flex items-center"
        >
          <span data-nm="wordmark">
            <img
              src={desktopWordmarkSrc}
              alt=""
              className="h-6 w-auto object-contain"
              style={{ maxWidth: NAVBAR_WORDMARK_LOGO_MAX_W_PX }}
            />
          </span>
          <span data-nm="icon">
            <img
              src={desktopIconSrc}
              alt=""
              className="h-6 w-auto object-contain"
              style={{ maxWidth: NAVBAR_ICON_LOGO_MAX_W_PX }}
            />
          </span>
          <span data-nm="more" className="flex items-center gap-1 whitespace-nowrap text-sm font-semibold">
            {NAV_OVERFLOW_MORE_LABEL}
            <ChevronDown className="h-3.5 w-3.5" />
          </span>
          <div data-nm="items" className="flex items-center" style={{ gap: NAVBAR_DESKTOP_GAP_PX }}>
            {navItems.map((item, idx) => renderMeasureItem(item, idx))}
          </div>
        </div>
      </div>

      {/* Mobile navigation menu */}
      {isPublic ? (
        // Public view: full-screen overlay, portaled to <body> so it escapes the sticky nav's
        // stacking context (z-40) and can cover the live banner (z-50) instead of rendering
        // beneath it. `fixed inset-0` is also immune to the sticky/overflow interaction (the
        // scroll-lock effect un-sticks the nav), so it always opens where the user is.
        isMounted &&
        createPortal(
          <div
            className={`fixed inset-0 z-[60] flex flex-col transition-opacity duration-200 ${mobileOnlyClass} ${
              isMobileMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            style={{ backgroundColor: navBgColor }}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            aria-hidden={!isMobileMenuOpen}
          >
            {/* Overlay header mirrors the navbar bar (logo + close) */}
            <div
              className={`flex h-14 shrink-0 items-center justify-between border-b px-4 ${navPaddingClass}`}
              style={{ borderColor }}
            >
              <div
                className="flex cursor-pointer items-center gap-2 transition-opacity hover:opacity-80"
                role="button"
                tabIndex={isMobileMenuOpen ? 0 : -1}
                aria-label="Scroll to top"
                onClick={handleLogoClick}
                onKeyDown={handleLogoKeyDown}
              >
                <img src={mobileLogoSrc} alt="Logo" className="h-6 w-auto max-w-[160px] object-contain" />
              </div>
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex touch-manipulation items-center justify-center rounded-md p-2 text-gray-600 transition-colors hover:text-gray-900"
                aria-label="Close menu"
                tabIndex={isMobileMenuOpen ? 0 : -1}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {/* Scrollable links */}
            <div
              className="flex-1 space-y-1 overflow-y-auto px-2 pb-3 pt-2"
              style={{
                WebkitOverflowScrolling: "touch",
                overscrollBehavior: "contain",
                paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 24px))",
              }}
            >
              {mobileMenuLinks}
            </div>
          </div>,
          document.body
        )
      ) : (
        // Editor canvas: inline accordion, so it stays inside the canvas frame.
        <div
          ref={mobileMenuRef}
          onTransitionEnd={handleTransitionEnd}
          className={`overflow-hidden border-t transition-[max-height] duration-300 ease-in-out ${mobileOnlyClass}`}
          style={{ maxHeight: 0, backgroundColor: navBgColor, borderColor }}
        >
          <div
            className="space-y-1 overflow-y-auto px-2 pb-3 pt-2"
            style={{
              maxHeight: MOBILE_MENU_MAX_HEIGHT_CSS,
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 24px))",
            }}
          >
            {mobileMenuLinks}
          </div>
        </div>
      )}
    </nav>
  );
}
